import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, DollarSign, ArrowRight, HandCoins } from "lucide-react";

/**
 * interface GivingTeaserProps {
 *   totalGiven?: string;
 *   recentDonors?: number;
 * }
 */
export default function GivingTeaser({ totalGiven = "$42,500", recentDonors = 124 }) {
  return (
    <section data-testid="giving-teaser-section" className="py-24 bg-bone-dark relative overflow-hidden">
      {/* Decorative background shapes */}
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-gold/5 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-navy/5 rounded-full blur-[80px] translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="container-page relative z-10 max-w-4xl">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6 shadow-sm border border-gold/30">
            <Heart className="h-8 w-8 text-gold" />
          </div>
          
          <div className="gold-line-center mb-6" />
          
          <h2 className="font-heading text-4xl md:text-5xl text-navy mb-6 leading-tight">
            Sow Into the Kingdom
          </h2>
          
          <p className="text-gray-600 text-lg md:text-xl mb-4 max-w-2xl mx-auto leading-relaxed">
            Your generosity sows seeds into Word Alive Global Center and plants a harvest for the
            <strong className="text-navy font-bold"> Jerry O'Neal Scholarship Fund</strong> — helping students answer their God-given calling.
          </p>

          <p className="text-gold/80 font-heading text-lg md:text-xl max-w-xl mx-auto italic mb-10">
            "Give, and it will be given to you — pressed down, shaken together and running over." <br className="hidden sm:block" />
            <span className="text-sm not-italic font-sans font-bold tracking-widest uppercase mt-2 inline-block">— Luke 6:38</span>
          </p>

          {/* Impact Stats (Progressive Disclosure) */}
          <div className="bg-white/60 backdrop-blur-sm border border-bone-dark rounded-2xl p-6 max-w-lg mx-auto mb-10 shadow-sm flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-12 divide-y sm:divide-y-0 sm:divide-x divide-bone-dark">
            <div className="text-center w-full sm:w-auto pt-2 sm:pt-0">
              <div className="flex items-center justify-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-gold" />
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Monthly Goal Progress</span>
              </div>
              <p className="font-heading text-2xl text-navy font-bold">{totalGiven}</p>
            </div>
            
            <div className="text-center w-full sm:w-auto pt-4 sm:pt-0 sm:pl-12">
              <div className="flex items-center justify-center gap-2 mb-1">
                <HandCoins className="h-4 w-4 text-gold" />
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Recent Faith Seeds</span>
              </div>
              <p className="font-heading text-2xl text-navy font-bold">{recentDonors} Families</p>
            </div>
          </div>

          {/* CTA */}
          <Link to="/giving" className="inline-block">
            <Button 
              data-testid="give-now-btn" 
              className="bg-gold text-navy hover:bg-gold/90 shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all duration-300 hover:-translate-y-1 rounded-full px-12 h-14 uppercase tracking-wider text-sm font-bold group"
            >
              Give Now
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          
          <p className="text-xs text-gray-400 mt-6 max-w-sm mx-auto uppercase tracking-wider font-bold">
            Zero Platform Fees. 100% Kingdom Impact.
          </p>
        </div>
      </div>
    </section>
  );
}